/**
 * ModalSlideout - A reusable, accessible, and animated slide-out modal component.
 *
 * Features:
 * - Automatically finds and connects to its trigger element via `aria-controls`.
 * - Closes when clicking the overlay or pressing "Escape" (unless disabled).
 * - Supports multiple animation directions.
 * - Moves itself into `<body>` to prevent z-index issues.
 * - Ensures only one modal is open at a time.
 *
 * Accessibility:
 * - Uses `aria-controls` to link the trigger button.
 * - Updates `aria-expanded` on the trigger element when opened/closed.
 * - Adds `aria-modal="true"` and `role="dialog"` for proper accessibility.
 *
 * Notes:
 * - If multiple modals exist, opening a new one will close any currently open modals.
 * - The modal is automatically moved to `<body>` for correct stacking.
 */

class ModalSlideout extends HTMLElement {
  static overlay = null;

  constructor() {
    super();
    this.isOpen = false;
    this.triggerElement = null;
    this.bindEvents();
  }

  /**
   * Validates if the modal contains the required `.slideout` structure
   * @returns {boolean} - Returns true if valid, false if invalid
   */
  validateStructure() {
    if (!this.querySelector('.slideout')) {
      console.warn(`Skipping initialization: modal-slideout (id="${this.id}") is missing a .slideout wrapper.`);
      this.remove();
      return false;
    }
    return true;
  }

  /**
   * Moves the modal to the <body> to avoid z-index issues
   */
  moveToBody() {
    // Don't move bottom sheet modal on desktop
    if (!window.utilities.isMobileBreakpoint() && this.animation === 'slide-bottom') return;

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  /**
   * Creates a shared overlay if it doesn't exist
   */
  initializeOverlay() {
    if (!ModalSlideout.overlay) {
      ModalSlideout.overlay = document.createElement('div');
      ModalSlideout.overlay.classList.add('slideout-modal-overlay');
      ModalSlideout.overlay.style.display = 'none';
      document.body.appendChild(ModalSlideout.overlay);

      ModalSlideout.overlay.addEventListener('click', () => {
        document.querySelectorAll('modal-slideout[open]').forEach((modal) => modal.close());
      });
    }
  }

  /**
   * Defines all the elements inside the component
   * @returns {Object} Object containing references to key elements
   */
  getViewModel() {
    return {
      $modal: this.querySelector('.slideout'),
      $closeBtn: this.querySelectorAll('.close-btn'),
    };
  }

  initializeAttributes() {
    this.closeOnEsc = this.getAttribute('close-on-esc') !== 'false';
    this.animation = this.getAttribute('animation') || 'slide-right';
  }

  bindEvents() {
    if (!this.validateStructure()) return;

    this.removeAttribute('hidden');
    this.initializeAttributes();
    this.moveToBody();
    this.initializeOverlay();

    this.viewModel = this.getViewModel();

    if (this.hasAttribute('open')) {
      this.open();
    }

    if (this.viewModel.$closeBtn) {
      this.viewModel.$closeBtn.forEach((cb) => {
        cb.addEventListener('click', () => this.close());
      });
    }

    if (this.closeOnEsc) {
      document.addEventListener('keydown', (e) => this.handleEscape(e));
    }

    window.utilities?.NavigationKeyHandler.setup({
      closeModal: () => this.isOpen && this.close(),
    });

    this.findTriggerElements();
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  /**
   * Finds all trigger buttons based on `aria-controls` and adds event listeners.
   */
  findTriggerElements() {
    const triggers = document.querySelectorAll(`[aria-controls="${this.id}"]`);

    if (!triggers.length) return;

    this.triggerElements = Array.from(triggers);

    this.triggerElements.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(trigger));
    });
  }

  open(trigger) {
    this.triggerElement = trigger;

    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'true');
    }

    this.closeAll(this.id);
    this.setAttribute('open', '');
    this.isOpen = true;
    ModalSlideout.overlay.style.display = 'block';

    window.utilities?.trapFocus(this);
    this._outsideClickHandler = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener('mousedown', this._outsideClickHandler);
  }

  close(id = null) {
    // we are adding a lot of these events and we need to remove them always
    if (this._outsideClickHandler) {
      document.removeEventListener('mousedown', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }
    // if we are opening a popup we shouldn't close it
    if (id && this.id === id) return;

    this.removeAttribute('open');
    this.isOpen = false;

    if (!document.querySelector('modal-slideout[open]')) {
      ModalSlideout.overlay.style.display = 'none';
    }
    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'false');
      this.triggerElement.classList.remove('is-active');
      this.triggerElement.focus();
    }
  }

  disableAnimationTemporary() {
    this.classList.add('no-animation');

    setTimeout(() => {
      this.classList.remove('no-animation');
    }, 400);
  }

  closeAll(id = null) {
    const openModals = document.querySelectorAll('modal-slideout[open]');

    if (openModals.length) {
      // On a mobile device, when one slider opens, a new one should open without animation.
      if (window.utilities.isMobileBreakpoint()) this.disableAnimationTemporary();

      document.querySelectorAll('modal-slideout[open]').forEach((modal) => {
        modal.close(id);
      });
    }
  }
}

customElements.define('modal-slideout', ModalSlideout);
